<template>
    <div class="riveStateMachine">
        <canvas ref="refCanvas" />
    </div>
</template>

<script setup>
    import { Alignment, Fit, Layout, Rive } from '@rive-app/canvas';
    import { onMounted, onUnmounted, ref, shallowRef, watch } from 'vue';

    import { useViewportResize } from '@resn/gozer-vue';

    const props = defineProps({
        src: { required: true },
        fit: { default: Fit.Contain },
        alignment: { default: Alignment.Center },
        artboard: { default: null },
        stateMachines: { default: null },
        animations: { default: null },
        autoPlay: { default: true },
        autoBind: { default: false },
        initOnMount: { default: true },
        overrideDrawingSurface: { default: false },
    });

    const emits = defineEmits(['loop', 'load']);

    // INIT
    const refCanvas = ref(null);

    const instance = shallowRef(null);
    const inputs = shallowRef([]);
    const vmi = shallowRef(null);

    const loaded = ref(false);

    const init = function () {
        destroy();

        return new Promise((resolve) => {
            instance.value = new Rive({
                canvas: refCanvas.value,
                src: props.src,
                artboard: props.artboard,
                stateMachines: props.stateMachines,
                layout: new Layout({
                    fit: props.fit,
                    alignment: props.alignment,
                }),
                autoplay: props.autoPlay,
                autoBind: props.autoBind,
                onLoad: () => {
                    inputs.value = instance.value.stateMachineInputs(props.stateMachines);
                    vmi.value = instance.value.viewModelInstance; // view model instance

                    loaded.value = true;

                    // resizeDrawingSurfaceToViewport(viewport.width, viewport.height);
                    resize({ width: viewport.width, height: viewport.height });

                    resolve();
                    emits('load');
                },
                onLoop: () => {
                    emits('loop');
                },
            });
        });
    };
    onMounted(() => (props.initOnMount ? init() : null));
    watch(() => props.src, init);

    // RESIZE
    const resizeDrawingSurfaceToViewport = (width, height) => {
        if (!instance.value || !refCanvas.value) return;

        const w = width ?? refCanvas.value.offsetWidth;
        const h = height ?? refCanvas.value.offsetHeight;

        if (w > 0 && h > 0) {
            const dpr = window.devicePixelRatio || 1;

            instance.value.devicePixelRatioUsed = dpr;

            instance.value.canvas.width = dpr * w;
            instance.value.canvas.height = dpr * h;

            instance.value.resizeToCanvas();
            instance.value.drawFrame();

            // Handle Fit.Layout case
            if (instance.value.layout.fit === Fit.Layout) {
                const scaleFactor = instance.value._layout.layoutScaleFactor;
                instance.value.artboard.width = w / scaleFactor;
                instance.value.artboard.height = h / scaleFactor;
            }
        }
    };
    // RESIZE
    const resize = function ({ width, height }) {
        if (!instance.value) return;

        if (props.overrideDrawingSurface) resizeDrawingSurfaceToViewport(width, height);
        else instance.value.resizeDrawingSurfaceToCanvas();
    };
    const viewport = useViewportResize(resize);

    const destroy = function () {
        instance.value?.cleanup();
    };

    // CLEANUP
    onUnmounted(() => {
        destroy();
    });

    // STATE

    const play = function () {
        instance.value?.play(props.stateMachines);
        instance.value?.startRendering?.();
    };

    const pause = () => {
        instance.value?.pause();
        instance.value?.stopRendering?.();
    };

    defineExpose({
        resize,
        instance,
        inputs,
        vmi,
        play,
        pause,
    });
</script>

<style lang="scss" scoped>
    @use '@/styles/shared.scss' as *;

    .riveStateMachine {
        canvas {
            @include fill;
        }
    }
</style>
